import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { whichCountryIsGayerFaqs } from '../components/FAQSection';
import { flagSrcSet, flagUrl } from '../data/countries';
import {
  buildComparisonPool,
  formatEntryValue,
  getEntryKey,
  getEntryValue,
  getMetricLabel,
  getMetricPromptWord,
  isHigherCorrect,
  pickComparisonPair,
  pickNextComparison,
} from '../data/gayFriendlyCountries';
import { getWhichCountryIsGayerRank, pickWrongAnswerRoast } from '../utils/whichCountryIsGayerRoasts';
import { getItem, setItem } from '../utils/safeStorage';
import GameGuideLink from '../components/GameGuideLink';
import '../styles/HigherOrLower.css';
import '../styles/WhichCountryIsGayer.css';

const HIGHSCORE_KEY = 'wcg-highscore';
const REVEAL_MS = 1400;

const randomPrompt = () => (Math.random() < 0.5 ? 'gayer' : 'less');

function EntryVisual({ entry }) {
  return (
    <div className="hol-flag">
      <img
        src={flagUrl(entry.code)}
        srcSet={flagSrcSet(entry.code)}
        alt={`${entry.name} flag`}
        width="80"
        height="80"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function RevealedValue({ value, active, formatValue }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) {
      setDisplay(0);
      return undefined;
    }
    const start = performance.now();
    const duration = 700;
    let frame;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(value * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, active]);

  return <p className="hol-value">{formatValue(display)}</p>;
}

export default function WhichCountryIsGayerPage() {
  const pool = useMemo(() => buildComparisonPool(), []);
  const poolRef = useRef(pool);

  const [{ left, right }, setPair] = useState(() => pickComparisonPair(pool));
  const [prompt, setPrompt] = useState(() => randomPrompt());
  const [score, setScore] = useState(0);
  const [highscore, setHighscore] = useState(0);
  const [phase, setPhase] = useState('playing');
  const [wasCorrect, setWasCorrect] = useState(null);
  const [pickedSide, setPickedSide] = useState(null);
  const [wrongLine, setWrongLine] = useState(null);
  const [resultRank, setResultRank] = useState(null);
  const timeoutRef = useRef(null);
  const pairRef = useRef({ left, right });
  const scoreRef = useRef(0);
  const promptRef = useRef(prompt);

  poolRef.current = pool;
  pairRef.current = { left, right };
  scoreRef.current = score;
  promptRef.current = prompt;

  useEffect(() => {
    const stored = Number(getItem(HIGHSCORE_KEY));
    if (Number.isFinite(stored) && stored > 0) setHighscore(stored);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const startGame = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const nextPair = pickComparisonPair(poolRef.current);
    setPair(nextPair);
    setPrompt(randomPrompt());
    setScore(0);
    setPhase('playing');
    setWasCorrect(null);
    setPickedSide(null);
    setWrongLine(null);
    setResultRank(null);
  }, []);

  const handlePick = useCallback((side) => {
    if (phase !== 'playing') return;

    const current = pairRef.current;
    const mode = promptRef.current;
    const leftValue = getEntryValue(current.left);
    const rightValue = getEntryValue(current.right);
    const pickedValue = side === 'left' ? leftValue : rightValue;
    const otherValue = side === 'left' ? rightValue : leftValue;
    const correct = isHigherCorrect(pickedValue, otherValue, mode);

    setPickedSide(side);
    setWasCorrect(correct);
    setPhase('revealing');

    if (correct) {
      const nextScore = scoreRef.current + 1;
      setScore(nextScore);
      setWrongLine(null);
      setHighscore((best) => {
        if (nextScore <= best) return best;
        setItem(HIGHSCORE_KEY, String(nextScore));
        return nextScore;
      });
    } else {
      setWrongLine(pickWrongAnswerRoast());
      setResultRank(getWhichCountryIsGayerRank(scoreRef.current));
    }

    timeoutRef.current = setTimeout(() => {
      if (correct) {
        const staying = side === 'left' ? current.left : current.right;
        const leaving = side === 'left' ? current.right : current.left;
        const nextRight = pickNextComparison(
          poolRef.current,
          staying,
          [getEntryKey(leaving)]
        );
        setPair({ left: staying, right: nextRight });
        setPrompt(randomPrompt());
        setWasCorrect(null);
        setPickedSide(null);
        setPhase('playing');
      } else {
        setPhase('gameover');
      }
    }, REVEAL_MS);
  }, [phase]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePick('left');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handlePick('right');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePick]);

  const revealing = phase === 'revealing' || phase === 'gameover';
  const playing = phase === 'playing';
  const resultClass = wasCorrect == null ? '' : wasCorrect ? 'is-correct' : 'is-wrong';
  const seo = getPageSeo('which-country-is-gayer');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(whichCountryIsGayerFaqs)]
    : buildFAQSchema(whichCountryIsGayerFaqs);
  const sameMetric = left.metric === right.metric;
  const promptMetric = sameMetric ? getMetricPromptWord(left) : 'number';
  const promptWord = prompt === 'less' ? 'less gay' : 'gayer';
  const numberHint = prompt === 'less' ? 'lower number' : 'higher number';

  const sideProps = (side) => {
    const entry = side === 'left' ? left : right;
    const metric = getMetricLabel(entry);
    return {
      role: 'button',
      tabIndex: playing ? 0 : -1,
      onClick: () => handlePick(side),
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlePick(side);
        }
      },
      className: [
        'hol-side',
        `hol-side-${side}`,
        playing ? 'is-pickable' : '',
        pickedSide === side ? resultClass : '',
      ].filter(Boolean).join(' '),
      'aria-label': `Pick ${entry.name} ${metric} as the ${promptWord} ${sameMetric ? metric.toLowerCase() : 'number'}`,
    };
  };

  const renderEntry = (entry, side) => {
    const formatValue = (value) => formatEntryValue(entry, value);
    const metric = getMetricLabel(entry);

    return (
      <>
        <EntryVisual entry={entry} />
        <h2 className="hol-country">{entry.name}</h2>
        <p className="hol-metric">{metric}</p>
        {side === 'right' && !revealing ? (
          <p className="hol-value hol-value-unknown">?</p>
        ) : side === 'right' && revealing ? (
          <div className="hol-reveal">
            <RevealedValue
              key={getEntryKey(entry)}
              value={getEntryValue(entry)}
              active={revealing}
              formatValue={formatValue}
            />
            <p className={`hol-result-label ${resultClass}`}>
              {wasCorrect ? 'Correct!' : (wrongLine || 'Wrong!')}
            </p>
          </div>
        ) : (
          <p className="hol-value">{formatEntryValue(entry, getEntryValue(entry))}</p>
        )}
      </>
    );
  };

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="hol-game" aria-label="Which Country Is Gayer? game">
        <h1 className="hol-visually-hidden">Which Country Is Gayer?</h1>

        <header className="hol-header">
          <div className="hol-scores">
            <div className="hol-score-block hol-score-high">
              <span className="hol-score-number">{highscore}</span>
              <span className="hol-score-label">Highscore</span>
            </div>
            <div className="hol-score-block hol-score-current">
              <span className="hol-score-number">{score}</span>
              <span className="hol-score-label">Score</span>
            </div>
          </div>
        </header>

        <div className="hol-board">
          <article {...sideProps('left')}>
            {renderEntry(left, 'left')}
          </article>

          <div className="hol-divider">
            <div className="hol-prompt wcg-prompt" aria-live="polite" data-prompt={prompt}>
              <span className="hol-prompt-line">Which {sameMetric ? promptMetric : 'number'}</span>
              <span className="hol-prompt-line hol-prompt-emphasis">is {promptWord}?</span>
              <span className="wcg-prompt-hint">{numberHint}</span>
            </div>
          </div>

          <article {...sideProps('right')}>
            {renderEntry(right, 'right')}
          </article>
        </div>

        {phase === 'gameover' && (
          <div className="hol-overlay" role="dialog" aria-labelledby="wcg-gameover-title">
            <div className="hol-overlay-card">
              <h2 id="wcg-gameover-title">Game Over</h2>
              <p className="hol-overlay-score">Score: {score}</p>
              <p className="hol-overlay-best">Best: {highscore}</p>
              {resultRank && (
                <>
                  <div className="hol-rank-row">
                    <span className="hol-rank-label">Your Rank:</span>
                    <span className="hol-rank-animal">{resultRank.animal}</span>
                  </div>
                  <p className="hol-overlay-roast">{resultRank.message}</p>
                </>
              )}
              <button type="button" className="hol-btn hol-btn-replay" onClick={startGame}>
                Play Again
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="hol-extras">
        <h2>How to Play Which Country Is Gayer?</h2>
        <p>
          Two countries. One number each. The center asks which is gayer (higher
          number) or less gay (lower number). The labels are gay-friendly metrics —
          Rainbow Index, Equality Index, legal protection, marriage-equality year,
          or estimated LGBT+ identification — but you still compare the numbers as
          printed. If Spain’s Rainbow Index is 89 and Nigeria’s Equality Index is
          11, 89 is gayer because it is the bigger number. Once you are stuck on
          the friendliest country, the prompt will ask “less gay” so the streak
          can go down.
        </p>
        <ul>
          <li>Correct pick stays on the left. A new challenger arrives on the right.</li>
          <li>Wrong pick ends the run. Your best score is saved in this browser.</li>
          <li>Click a side, or use the left and right arrow keys.</li>
          <li>This is a rights-index trivia game, not a stereotype contest.</li>
        </ul>
      </section>

      <GameGuideLink slug="which-country-is-gayer" gameName="Which Country Is Gayer?" />
      <FAQSection mode="whichCountryIsGayer" />
    </>
  );
}
